import React, { useContext } from "react";
import PropTypes from "prop-types";
import { CurrencyContext } from "../../../contexts/Currencycontext";
import { useParams, useLocation } from "react-router-dom";

// Summer period: June 15 - August 31
const isSummerDate = (date) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  if (month === 6 && day >= 15) return true;
  if (month === 7) return true;
  if (month === 8 && day <= 31) return true;
  return false;
};

const CarCard = ({ images, title, price, ratings, type, isNewCar, categories }) => {
  const image = process.env.REACT_APP_BASE_URL + "uploads/" + images[0];
  const { currency, convertPrice } = useContext(CurrencyContext);
  const { category: paramCategory } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const queryCategory = urlParams.get("category");

  const category = paramCategory || queryCategory;
  const pathSegments = location.pathname.split("/");
  const pathCategory = pathSegments[pathSegments.length - 1];
  const effectiveCategory = category || (pathCategory !== "search" ? pathCategory : null);

  const isLongueDuree = effectiveCategory === "longueduree";
  const priceUnit = isLongueDuree ? "Mois" : "Jour";

  // Get dates from URL if available
  const pickupDate = urlParams.get("pickupDate");
  const dropoffDate = urlParams.get("dropoffDate");

  const getCategoryData = () => {
    if (categories && categories.length > 0) {
      const match = categories.find(
        (cat) => cat.type === effectiveCategory || cat.categoryType === effectiveCategory
      );
      return match || categories[0];
    }
    return null;
  };

  // Check if any day in the range falls in summer
  const hasSummerDays = () => {
    if (!pickupDate || !dropoffDate) return false;
    const start = new Date(pickupDate);
    const end = new Date(dropoffDate);
    const current = new Date(start);
    while (current < end) {
      if (isSummerDate(current)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  // Calculate display price considering summer pricing
  const getDisplayPrice = () => {
    const catData = getCategoryData();
    if (!catData) return price;

    const normalPrice = catData.price;
    const summerPrice = catData.summerPrice;

    // No dates selected or no summer price — show normal price
    if (!pickupDate || !dropoffDate || !summerPrice) return normalPrice;

    // Count normal vs summer days
    const start = new Date(pickupDate);
    const end = new Date(dropoffDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    let normalDays = 0;
    let summerDays = 0;
    const current = new Date(start);
    while (current < end) {
      if (isSummerDate(current)) summerDays++;
      else normalDays++;
      current.setDate(current.getDate() + 1);
    }

    const totalDays = normalDays + summerDays;
    if (totalDays === 0) return normalPrice;

    if (isLongueDuree) {
      // Longue durée: per month
      const total = (normalDays / 30) * normalPrice + (summerDays / 30) * summerPrice;
      const totalMonths = totalDays / 30;
      return totalMonths > 0 ? total / totalMonths : normalPrice;
    } else {
      // Courte durée: per day average
      const total = normalDays * normalPrice + summerDays * summerPrice;
      return total / totalDays;
    }
  };

  const averageRating = ratings.length > 0
    ? Math.round(ratings.reduce((sum, { rating }) => sum + rating, 0) / ratings.length)
    : 0;

  const displayPrice = getDisplayPrice();
  const isSummer = hasSummerDays() && getCategoryData()?.summerPrice;

  return (
    <div className='relative bg-grey rounded-3xl shadow-lg w-full max-w-xs sm:max-w-sm mx-auto'>
      {isNewCar && (
        <div className='absolute top-0 right-0 bg-orange-light text-white text-xs font-semibold uppercase tracking-wide px-3 py-1 shadow-lg rounded-lg rounded-tl-none rounded-br-none'>
          Nouvelle
        </div>
      )}

      {/* Summer badge */}
      {isSummer && (
        <div
          className='absolute top-0 left-0 text-white text-xs font-semibold px-3 py-1 shadow-lg rounded-lg rounded-tr-none rounded-bl-none'
          style={{ background: '#f59e0b' }}
        >
          🌞 Prix été
        </div>
      )}

      <img src={image} alt={title} className='object-cover w-full aspect-[16/9] rounded-t-3xl' />

      <div className='p-4'>
        <div className='flex flex-col sm:flex-row justify-between'>
          <div className='mb-2 sm:mb-0'>
            <h3 className='text-lg sm:text-xl font-bold mb-2'>{title}</h3>
            <div className='flex items-center'>
              <span className='text-yellow-light mr-2'>
                {"★".repeat(averageRating)}{"☆".repeat(5 - averageRating)}
              </span>
            </div>
          </div>

          <div className='flex flex-col items-end justify-center'>
            <p className='text-gray-700 text-sm sm:text-base font-semibold'>
              À partir de {convertPrice(displayPrice)} {currency}/{priceUnit}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

CarCard.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ratings: PropTypes.arrayOf(PropTypes.shape({ rating: PropTypes.number.isRequired })).isRequired,
  type: PropTypes.string.isRequired,
  isNewCar: PropTypes.bool.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({
    categoryType: PropTypes.string,
    type: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    summerPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    available: PropTypes.bool,
  })),
};

export default CarCard;